import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CalendarEvent, EventCalenderProps } from '../../../../shared/models/hospitalProfile.model';
import AddCalendarForm from '../../shared/AddCalendarForm';
import { DATE_FORMAT, HOLIDAY_CAL_EVENT, NORMAL_CAL_EVENT, SPECIAL_CAL_EVENT } from '../../../../shared/constants/commonConstants';
import { CAL_EVENTS_CATEGORY } from './../../../../shared/constants/commonConstants';
import { Tooltip } from 'antd';
import { capitalizeFirstLetter, centeredNotification } from '../../../../utils/utility';

const EventCalender = ({ events, onSaveCalendar }: EventCalenderProps): ReactElement => {
  const localizer = momentLocalizer(moment);
  const [calEvents, setCalEvents] = useState(events);
  const [isNewEvent, setIsNewEvent] = useState(false);
  const [viewEvent, setViewEvent] = useState({} as any);
  const [defaultDates, setDefaultDates] = useState({ start: new Date(), end: new Date() });

  useEffect((): void => {
    if (events) {
      setCalEvents([...events]);
    }
  }, [events]);

  const handleSelect = ({ start, end }: any): void => {
    if (!moment(start).isBefore(moment().startOf('day'))) {
      setDefaultDates({
        start: moment(start).isBefore(new Date()) ? new Date() : start,
        end:  moment(end).isBefore(new Date()) ? new Date() : end,
      });
      setViewEvent(null);
      setIsNewEvent(true);
    } else {
      centeredNotification('Backdated event creation is not allowed.');
    }
  };

  const onOk = (): void => {
    setIsNewEvent(false);
    onSaveCalendar(viewEvent ? true : false);
  };

  const onCancel = (): void => {
    setIsNewEvent(false);
  };

  const onViewEvent = ({ resource: { eventInfo } }: CalendarEvent): void => {
    if (eventInfo.typeId !== CAL_EVENTS_CATEGORY[0] && eventInfo.typeId !== 0) {
      return;
    } else {
      setIsNewEvent(true);
      setViewEvent(eventInfo);
    }
  };

  const customEventPropGetter = ({ resource: { eventInfo: { typeId } } }: CalendarEvent): any => {
    if (typeId === CAL_EVENTS_CATEGORY[1]) {
      return { className: HOLIDAY_CAL_EVENT };
    } else if (typeId === CAL_EVENTS_CATEGORY[2]) {
      return { className: SPECIAL_CAL_EVENT };
    } else {
      return { className: NORMAL_CAL_EVENT };
    }
  };

  function Event({ event: { title, desc, start, end } }: any): ReactElement {
    return (
      <span>
        <Tooltip title={getTooltipContent(title, desc, start, end)}>
          <strong>{title}</strong>
        </Tooltip>
        {desc && ':  ' + desc}
      </span>
    );
  }

  const getTooltipContent = (title: string, desc: string, start: Date, end: Date): ReactNode => (
    <>
      <p>{`${title} from ${moment(start).format(DATE_FORMAT)} to ${moment(end).format(DATE_FORMAT)}`}</p>
      <p className="cal-tooltip-desc">{capitalizeFirstLetter(desc)}</p>
    </>
  );

  return (
    <React.Fragment>
      <Calendar selectable={true}
        eventPropGetter={customEventPropGetter}
        components={{
          event: Event,
        }}
        localizer={localizer}
        tooltipAccessor={(): any => null}
        events={calEvents}
        popup={true}
        views={{ day: true, week: true, month: true }}
        onSelectEvent={onViewEvent}
        onSelectSlot={handleSelect} />
      {isNewEvent && (
        <AddCalendarForm
          visible={isNewEvent}
          onCancel={onCancel}
          onOk={onOk}
          title={'Event Details'}
          data={viewEvent}
          eventId={viewEvent?.id} defaultDate={defaultDates} />
      )}
    </React.Fragment>

  );
};

export default EventCalender;
